import React, { FC, useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface CountdownProps {
	/** Target time to count down to – Unix epoch time (in seconds) */
	targetDate: number;
	/** 'medium' uses the existing look, 'small' renders a compact timer */
	size?: 'medium' | 'small';
}

interface TimeParts {
	days: string;
	hours: string;
	minutes: string;
	seconds: string;
}

const shouldForwardProp = (prop: string) => prop !== 'size';

const TimerContainer = styled(Box)({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

const TimeBox = styled(Box, { shouldForwardProp })<{
	size: 'medium' | 'small';
}>(({ theme, size }) => ({
	border: `1px solid ${theme.palette.grey[400]}`,
	borderRadius: theme.shape.borderRadius,
	padding: size === 'small' ? theme.spacing(0.5, 1) : theme.spacing(1, 2),
	minWidth: size === 'small' ? 32 : 48,
	textAlign: 'center',
	margin: theme.spacing(0, size === 'small' ? 0.25 : 0.5),
	backgroundColor: '#fff',
}));

const Label = styled(Typography, { shouldForwardProp })<{
	size: 'medium' | 'small';
}>(({ theme, size }) => ({
	fontSize: theme.typography.pxToRem(size === 'small' ? 10 : 12),
	color: theme.palette.text.secondary,
	marginTop: theme.spacing(size === 'small' ? 0.25 : 0.5),
}));

const Separator = styled(Typography, { shouldForwardProp })<{
	size: 'medium' | 'small';
}>(({ theme, size }) => ({
	fontSize: theme.typography.pxToRem(size === 'small' ? 18 : 24),
	fontWeight: 500,
	margin: theme.spacing(0, size === 'small' ? 0.25 : 0.5),
}));

const CountdownTimer: FC<CountdownProps> = ({
	targetDate,
	size = 'medium',
}) => {
	const calculate = (): TimeParts => {
		const now = Date.now();
		const then = targetDate * 1000;
		const diff = Math.max(0, then - now);

		const days = Math.floor(diff / (1000 * 60 * 60 * 24));
		const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
		const minutes = Math.floor((diff / (1000 * 60)) % 60);
		const seconds = Math.floor((diff / 1000) % 60);

		const pad = (n: number) => String(n).padStart(2, '0');

		return {
			days: pad(days),
			hours: pad(hours),
			minutes: pad(minutes),
			seconds: pad(seconds),
		};
	};

	const [time, setTime] = useState<TimeParts>(calculate());

	useEffect(() => {
		const timer = setInterval(() => {
			setTime(calculate());
		}, 1000);

		setTime(calculate());
		return () => clearInterval(timer);
	}, [targetDate]);

	const numberVariant = size === 'small' ? 'subtitle2' : 'h6';

	// Abbreviations for small size
	const labels = {
		days: 'Days',
		hours: 'Hours',
		minutes: 'Minutes',
		seconds: 'Seconds',
	};

	return (
		<TimerContainer>
			<TimeBox size={size}>
				<Typography variant={numberVariant}>
					{time.days}
					{size === 'small' ? 'd' : ''}
				</Typography>
				{size !== 'small' && <Label size={size}>{labels.days}</Label>}
			</TimeBox>

			<Separator size={size}>:</Separator>

			<TimeBox size={size}>
				<Typography variant={numberVariant}>
					{time.hours}
					{size === 'small' ? 'h' : ''}
				</Typography>
				{size !== 'small' && <Label size={size}>{labels.hours}</Label>}
			</TimeBox>

			<Separator size={size}>:</Separator>

			<TimeBox size={size}>
				<Typography variant={numberVariant}>
					{time.minutes}
					{size === 'small' ? 'm' : ''}
				</Typography>
				{size !== 'small' && (
					<Label size={size}>{labels.minutes}</Label>
				)}
			</TimeBox>

			<Separator size={size}>:</Separator>

			<TimeBox size={size}>
				<Typography variant={numberVariant}>
					{time.seconds}
					{size === 'small' ? 's' : ''}
				</Typography>
				{size !== 'small' && (
					<Label size={size}>{labels.seconds}</Label>
				)}
			</TimeBox>
		</TimerContainer>
	);
};

export default CountdownTimer;
